// Week templates for the Plan tab (036_meal_templates.sql): a week of planned
// meals saved under a name, put back on any week later. The pure half —
// hooks/useMealTemplates.ts reads and writes them, components/meals/
// TemplatesSheet.tsx shows them.

import type { MealTemplate, PlannedMeal } from './supabase'
import { addDays, clampServings, dayKey, fromKey } from './mealPlan'

export type TemplateMeal = MealTemplate['meals'][number]

export const TEMPLATE_NAME_MAX = 60
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

/** 0 = Monday … 6 = Sunday, for a day of the week starting `monday`. */
const weekdayOf = (day: string, monday: string) =>
  Math.round((fromKey(day).getTime() - fromKey(monday).getTime()) / 86_400_000)

/** The week's meals as a template's, in plan order (day, then when planned). */
export function templateFromWeek(meals: Pick<PlannedMeal, 'day' | 'recipe_id' | 'servings' | 'created_at'>[], monday: string): TemplateMeal[] {
  return meals
    .map(m => ({ m, weekday: weekdayOf(m.day, monday) }))
    .filter(({ weekday }) => weekday >= 0 && weekday <= 6)
    .sort((a, b) => a.weekday - b.weekday || a.m.created_at.localeCompare(b.m.created_at))
    .map(({ m, weekday }) => ({ weekday, recipe_id: m.recipe_id, servings: clampServings(m.servings) }))
}

/**
 * What putting a template on the week of `monday` plans: one meal per entry,
 * leaving out recipes deleted since and meals that week already has (the same
 * recipe on the same day), so applying twice changes nothing.
 */
export function mealsFromTemplate(
  template: Pick<MealTemplate, 'meals'>,
  monday: string,
  existing: Pick<PlannedMeal, 'day' | 'recipe_id'>[],
  recipeIds: Set<string>,
): { day: string; recipe_id: string; servings: number }[] {
  const taken = new Set(existing.map(m => `${m.day}|${m.recipe_id}`))
  const out: { day: string; recipe_id: string; servings: number }[] = []
  for (const t of template.meals) {
    if (!recipeIds.has(t.recipe_id)) continue
    const day = dayKey(addDays(fromKey(monday), t.weekday))
    const slot = `${day}|${t.recipe_id}`
    if (taken.has(slot)) continue
    taken.add(slot)
    out.push({ day, recipe_id: t.recipe_id, servings: clampServings(t.servings) })
  }
  return out
}

/** "5 meals: Mon, Tue, Thu" — what a template holds, for its row. */
export function templateSummary(t: Pick<MealTemplate, 'meals'>, recipeIds?: Set<string>): string {
  const meals = recipeIds ? t.meals.filter(m => recipeIds.has(m.recipe_id)) : t.meals
  if (meals.length === 0) return 'No meals'
  const days = [...new Set(meals.map(m => m.weekday))].sort((a, b) => a - b).map(d => WEEKDAYS[d])
  return `${meals.length} ${meals.length === 1 ? 'meal' : 'meals'}: ${days.join(', ')}`
}

/** A saved template whose name this one would replace (case and spacing aside). */
export function sameNamed<T extends Pick<MealTemplate, 'name'>>(templates: T[], name: string): T | undefined {
  const key = name.trim().replace(/\s+/g, ' ').toLocaleLowerCase()
  return templates.find(t => t.name.trim().replace(/\s+/g, ' ').toLocaleLowerCase() === key)
}
